import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuFileSpreadsheet, LuHistory, LuUndo2 } from 'react-icons/lu';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GlassIcon from '../../../components/ui/GlassIcon';
import { EmptyState } from '../../ProfilePage/components/ProfileUi';
import useStockText from '../stockText';

const TYPE_TONES = {
  assignment: 'tw-bg-amber-500/10 tw-text-amber-700 dark:tw-text-amber-300',
  visit_usage: 'tw-bg-sky-500/10 tw-text-sky-700 dark:tw-text-sky-300',
  visit_return: 'tw-bg-emerald-500/10 tw-text-emerald-700 dark:tw-text-emerald-300',
  adjustment: 'tw-bg-violet-500/10 tw-text-violet-700 dark:tw-text-violet-300',
  return: 'tw-bg-emerald-500/10 tw-text-emerald-700 dark:tw-text-emerald-300',
  general: 'tw-bg-slate-500/10 tw-text-slate-600 dark:tw-text-slate-300',
};
const KNOWN_TYPES = new Set(Object.keys(TYPE_TONES));

const isVisit = (movement) => movement.type === 'visit_usage' || movement.type === 'visit_return';

const TypePill = ({ type }) => {
  const { label } = useStockText();
  const key = KNOWN_TYPES.has(type) ? type : 'general';
  return (
    <span className={`tw-inline-flex tw-whitespace-nowrap tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${TYPE_TONES[key]}`}>
      {type && !KNOWN_TYPES.has(type) ? type : label(`history.types.${key}`)}
    </span>
  );
};

TypePill.propTypes = { type: PropTypes.string };

const Quantity = ({ movement }) => {
  const isReturn = movement.type === 'visit_return' || movement.quantity < 0;
  return (
    <span className={`tw-font-bold tw-tabular-nums ${isReturn ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-rose-600 dark:tw-text-rose-300'}`}>
      {isReturn ? '+' : '-'}{Math.abs(movement.quantity)}
    </span>
  );
};

Quantity.propTypes = { movement: PropTypes.object.isRequired };

const Person = ({ movement }) => {
  const { label } = useStockText();
  if (!isVisit(movement)) return <span>{movement.employeeName || '-'}</span>;
  return (
    <span className="tw-block">
      <span className="tw-block tw-font-semibold">{movement.employeeName || label('history.unknownEmployee')}</span>
      <span className="tw-block tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('history.visit', { patient: movement.patientId || label('history.noPatient') })}</span>
    </span>
  );
};

Person.propTypes = { movement: PropTypes.object.isRequired };

/**
 * "Stok Hareketleri" of one stock: every assignment and visit usage/return,
 * newest first, with an Excel download. An assignment can be taken back
 * (the quantity returns to the stock and the record is removed).
 */
const StockHistoryModal = ({ isOpen, stock, history, onUndo, onExport, onClose }) => {
  const { t, language, label, formatDateTime } = useStockText();
  const [pendingUndo, setPendingUndo] = useState(null);

  const action = (movement) => {
    if (movement.type === 'assignment') {
      return (
        <Button size="sm" variant="secondary" onClick={() => setPendingUndo(movement)} title={label('history.undoHint')}>
          <LuUndo2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-emerald-600 dark:tw-text-emerald-300" />
          {label('history.undo')}
        </Button>
      );
    }
    return <span className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{isVisit(movement) ? label('history.visitMovement') : label('history.noAction')}</span>;
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        title={label('history.title')}
        subtitle={stock?.['Adı']}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30" size="xs">
            <LuHistory aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        actions={(
          <Button size="sm" onClick={onExport} disabled={history.length === 0}>
            <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
            {label('history.excel')}
          </Button>
        )}
        footer={<Button onClick={onClose}>{t('common.close')}</Button>}
      >
        {history.length === 0 ? (
          <EmptyState icon={LuHistory} title={label('history.empty')} />
        ) : (
          <>
            <div className="tw-relative tw-hidden tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar md:tw-block dark:tw-border-white/10">
              <table className="tw-w-full tw-border-collapse tw-text-sm">
                <thead className="tw-sticky tw-top-0 tw-z-[1] tw-bg-slate-50 dark:tw-bg-[#262a32]">
                  <tr>
                    {['date', 'person', 'quantity', 'type', 'note', 'action'].map((key) => (
                      <th key={key} scope="col" className={`tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400 ${key === 'quantity' || key === 'action' ? 'tw-text-center' : 'tw-text-left'}`}>
                        {label(`history.columns.${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-slate-200/70 tw-text-slate-700 dark:tw-divide-white/[0.06] dark:tw-text-slate-200">
                  {history.map((movement) => (
                    <tr key={movement.id} className="tw-bg-white dark:tw-bg-[#1f232b]">
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-tabular-nums">{formatDateTime(movement.date)}</td>
                      <td className="tw-whitespace-normal tw-px-4 tw-py-3"><Person movement={movement} /></td>
                      <td className="tw-px-4 tw-py-3 tw-text-center"><Quantity movement={movement} /></td>
                      <td className="tw-px-4 tw-py-3"><TypePill type={movement.type} /></td>
                      <td className="tw-whitespace-normal tw-px-4 tw-py-3 tw-text-slate-600 dark:tw-text-slate-300">{movement.note || '-'}</td>
                      <td className="tw-px-4 tw-py-2 tw-text-center">{action(movement)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="tw-space-y-2.5 md:tw-hidden">
              {history.map((movement) => (
                <li key={movement.id} className="tw-space-y-2 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/80 tw-p-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                  <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                    <div className="tw-min-w-0 tw-text-sm tw-text-slate-800 dark:tw-text-slate-100"><Person movement={movement} /></div>
                    <Quantity movement={movement} />
                  </div>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    <TypePill type={movement.type} />
                    <span className="tw-tabular-nums">{formatDateTime(movement.date)}</span>
                  </div>
                  {movement.note && <p className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{movement.note}</p>}
                  <div className="tw-flex tw-justify-end">{action(movement)}</div>
                </li>
              ))}
            </ul>
          </>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingUndo}
        tone="warning"
        icon={LuUndo2}
        title={label('history.confirmUndo.title')}
        message={label('history.confirmUndo.message', { quantity: pendingUndo?.quantity ?? '', stock: stock?.['Adı'] || '' })}
        confirmLabel={label('history.undo')}
        cancelLabel={label('common.cancel')}
        onConfirm={() => {
          const movement = pendingUndo;
          setPendingUndo(null);
          onUndo(movement);
        }}
        onCancel={() => setPendingUndo(null)}
      />
    </>
  );
};

StockHistoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  stock: PropTypes.object,
  history: PropTypes.array.isRequired,
  onUndo: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StockHistoryModal;
